import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Modal } from "react-bootstrap";
import { useTranslation } from 'react-i18next';

const PROFILE_TABS = [
  { type: "CLIENT", label: "Klienci", candidatesKey: "clients" },
  { type: "TEACHER", label: "Nauczyciele", candidatesKey: "teachers" },
];

function normalizeText(value) {
  return String(value || "").trim().toLowerCase();
}

function matchesSearch(candidate, searchText) {
  if (!searchText) return true;
  return [candidate.label, candidate.email].some(value => normalizeText(value).includes(searchText));
}

function getCandidateText(candidate) {
  return [candidate.label, candidate.email].filter(Boolean).join(" ");
}

function CreateAccountModal({
  accountCandidates,
  error,
  onClose,
  onCreate,
}) {
  const { t: tr } = useTranslation();
  const [activeProfileType, setActiveProfileType] = useState("CLIENT");
  const [searchText, setSearchText] = useState("");
  const [selectedId, setSelectedId] = useState("");
  const [temporaryPassword, setTemporaryPassword] = useState("");
  const [localError, setLocalError] = useState("");
  const activeTab = PROFILE_TABS.find(tab => tab.type === activeProfileType) || PROFILE_TABS[0];
  const candidates = accountCandidates[activeTab.candidatesKey] || [];
  const filteredCandidates = useMemo(() => {
    const normalizedSearch = normalizeText(searchText);
    return candidates.filter(candidate => matchesSearch(candidate, normalizedSearch));
  }, [candidates, searchText]);

  useEffect(() => {
    setSearchText("");
    setSelectedId("");
    setLocalError("");
  }, [activeProfileType]);

  const validate = () => {
    if (!selectedId) {
      setLocalError(tr("manage:extraSelectAProfile_43"));
      return false;
    }
    if (temporaryPassword.length < 8) {
      setLocalError(tr("auth:passwordShort"));
      return false;
    }
    setLocalError("");
    return true;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!validate()) return;

    onCreate({
      profileType: activeProfileType,
      profileId: selectedId,
      password: temporaryPassword,
    });
  };

  return (
    <Modal
      show
      size="lg"
      onHide={onClose}
      dialogClassName="create-account-modal"
      aria-labelledby="create-account-title"
      onEntered={node => {
        // This overlays version hides its own dialog and puts the label on the inner document.
        node.removeAttribute("aria-hidden");
        node.setAttribute("aria-labelledby", "create-account-title");
        node.setAttribute("aria-modal", "true");
      }}
    >
      <Modal.Header>
        <Modal.Title id="create-account-title">{tr("manage:createAccount_167")}</Modal.Title>
        <button type="button" className="close" onClick={onClose} aria-label={tr("common:close")}>
          <span aria-hidden="true">&times;</span>
        </button>
      </Modal.Header>
      <form onSubmit={handleSubmit}>
        <Modal.Body>
          {(localError || error) && (
          <div className="alert alert-danger" role="alert">{localError || error}</div>
          )}
          <div className="btn-group mb-3" role="tablist" aria-label={tr("manage:profileType_168")}>
            {PROFILE_TABS.map(tab => (
              <button
                key={tab.type}
                type="button"
                className={`btn ${activeProfileType === tab.type ? "manage-red-button" : "manage-teal-button"}`}
                onClick={() => setActiveProfileType(tab.type)}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <div className="form-group">
            <label htmlFor="create-account-search">{tr("manage:search_169")}<input
              id="create-account-search"
              className="form-control"
              value={searchText}
              onChange={event => setSearchText(event.target.value)}
            />
            </label>
          </div>
          <div className="list-group mb-3">
            {filteredCandidates.map(candidate => (
              <label className="list-group-item" key={candidate.id} htmlFor={`candidate-${candidate.id}`}>
                <input
                  id={`candidate-${candidate.id}`}
                  className="mr-2"
                  type="radio"
                  name="accountCandidate"
                  value={candidate.id}
                  checked={selectedId === candidate.id}
                  aria-label={getCandidateText(candidate)}
                  onChange={event => setSelectedId(event.target.value)}
                />
                <span>{candidate.label}</span>
                {candidate.email && <small className="ml-2 text-muted">{candidate.email}</small>}
              </label>
            ))}
            {filteredCandidates.length === 0 && <div className="text-muted">{tr("manage:noResults_170")}</div>}
          </div>
          <div className="form-group">
            <label htmlFor="temporary-password">{tr("manage:temporaryPassword_171")}<input
              id="temporary-password"
              className="form-control"
              type="password"
              value={temporaryPassword}
              onChange={event => setTemporaryPassword(event.target.value)}
            />
            </label>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn manage-button manage-teal-button" onClick={onClose}>{tr("common:cancel")}</button>
          <button type="submit" className="btn manage-button manage-red-button">{tr("manage:createAccount_167")}</button>
        </Modal.Footer>
      </form>
    </Modal>
  );
}

CreateAccountModal.propTypes = {
  accountCandidates: PropTypes.shape({
    clients: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      email: PropTypes.string,
    })),
    teachers: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      email: PropTypes.string,
    })),
  }).isRequired,
  error: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
};

CreateAccountModal.defaultProps = {
  error: "",
};

export default CreateAccountModal;
